import {Alert} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';
import {businessCaseRoutes, vehiclesRoutes} from '@dms/routes';

import {composePath} from 'shared/web';

import type {AuctionRule, AuctionView} from '../../model/auctionView.types';

// The BE opens the sale business case when the seller confirms the winner. These rules block it.
const SALE_CASE_FIX_ROUTE: Partial<
  Record<AuctionRule, typeof vehiclesRoutes.detail | typeof vehiclesRoutes.businessCases>
> = {
  SALE_PRICE_MISSING: vehiclesRoutes.detail,
  SALE_CURRENCY_MISMATCH: vehiclesRoutes.detail,
  SALE_BUSINESS_CASE_EXISTS: vehiclesRoutes.businessCases,
};

export function SaleCasePanel(props: {
  vehicleId: string;
  view: AuctionView;
  onRequestSaleBusinessCase: () => void;
}) {
  const saleBusinessCaseId = props.view.transaction?.saleBusinessCaseId ?? null;
  // The paid invoice closes the auction, so the invoice task ends after the payment.
  const isPaid = isNotNil(props.view.transaction?.paidAt);
  const blockers = props.view.blockers.filter(
    ({code, action}) => action === 'confirmWinner' && SALE_CASE_FIX_ROUTE[code] !== undefined
  );

  return match({
    saleBusinessCaseId,
    canRetry: props.view.allowedActions.includes('createSaleBusinessCase'),
  })
    .with({saleBusinessCaseId: Pattern.string}, ({saleBusinessCaseId: id}) => (
      <Alert
        type="inline"
        variant="success"
        title={i18n.t('entity.auction.confirmation.saleCase.title')}
        message={isPaid ? null : i18n.t('entity.auction.confirmation.saleCase.created')}
        hyperlinks={[
          {
            title: i18n.t('entity.auction.confirmation.saleCase.open'),
            href: composePath(businessCaseRoutes.overview, {
              params: {id},
              isSearchParamsPreserved: false,
            }),
          },
        ]}
        data-testid="auction-sale-case"
      />
    ))
    .with({canRetry: true}, () => (
      <Alert
        type="inline"
        variant="error"
        message={i18n.t('entity.auction.confirmation.saleCase.failed')}
        hyperlinks={[
          {
            title: i18n.t('entity.auction.confirmation.saleCase.retry'),
            onClick: props.onRequestSaleBusinessCase,
          },
        ]}
        data-testid="auction-sale-case-retry"
      />
    ))
    .otherwise(() => (
      <Show when={blockers.length > 0}>
        <VStack spacing={2} data-testid="auction-sale-case-blockers">
          {blockers.map((blocker) => (
            <Alert
              key={blocker.code}
              type="inline"
              variant="error"
              message={blocker.message}
              hyperlinks={[
                {
                  title: i18n.t('entity.auction.listing.fix'),
                  href: composePath(SALE_CASE_FIX_ROUTE[blocker.code] ?? vehiclesRoutes.detail, {
                    params: {id: props.vehicleId},
                    isSearchParamsPreserved: false,
                  }),
                },
              ]}
              data-testid={`auction-blocker-${blocker.code}`}
            />
          ))}
        </VStack>
      </Show>
    ));
}
